// Concierge payment requests on the client page: labels and amount wording.
// Pure + unit-tested. Amounts arrive in cents and are shown in dollars only.
import { centsToDollars } from './money';
import { fmtMonDay } from './conciergeActivityModel';

/** Axios config carrying the signed-in user's token, for the concierge-billing calls. */
export function authConfig() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
}

/** The API's error detail when it is a plain string, else `fallback` (a 422's
 * detail is a list of objects, which React cannot render). */
export function errorDetail(err, fallback) {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' && detail ? detail : fallback;
}

/** Shown wherever a paid payment plan has no monthly charges set up in Stripe. */
export const SCHEDULE_MISSING_TEXT = 'Monthly charges not set up — contact ProZpects';

const STATUS_LABELS = {
  sent: 'Awaiting payment',
  active: 'Paid up',
  past_due: 'Past due',
  complete: 'Paid in full',
  cancelled: 'Cancelled',
};

export function statusLabel(status) {
  return STATUS_LABELS[status] || status || '';
}

/** 480000 -> "$4,800.00". */
export function dollars(cents) {
  const plain = centsToDollars(cents);
  if (plain === '') return '';
  return `$${Number(plain).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** "3 of 9 monthly payments · next Feb 3" for a payment plan; "" for pay in full. */
export function progressText(req) {
  if (!req || req.pay_option !== 'plan' || !req.monthly_payment_count) return '';
  const base = `${req.payments_made || 0} of ${req.monthly_payment_count} monthly payments`;
  const next = req.next_charge_at ? fmtMonDay(req.next_charge_at) : '';
  return next ? `${base} · next ${next}` : base;
}

/** "$4,800" — dollars without a trailing ".00", for compact labels. */
const shortDollars = (cents) => dollars(cents).replace(/\.00$/, '');

/** Plan dropdown label: "Concierge Full 12 Month — $4,800 or $1,200 + 9 × $400/mo". */
export function planOptionLabel(plan) {
  const full = `${plan.name} — ${shortDollars(plan.full_price_cents)}`;
  if (!plan.offers_payment_plan || !plan.monthly_payment_count) return full;
  return `${full} or ${shortDollars(plan.upfront_cents)} + ${plan.monthly_payment_count} × `
    + `${shortDollars(plan.monthly_cents)}/mo`;
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** What the guardian will pay, worded as the request email words it. */
export function amountSummary(plan, payOption) {
  if (!plan) return '';
  if (payOption !== 'plan') return dollars(plan.full_price_cents);
  const offset = plan.first_charge_offset_months ?? 1;
  return `${dollars(plan.upfront_cents)} today, then ${dollars(plan.monthly_cents)}/month for `
    + `${plural(plan.monthly_payment_count, 'month')}, starting ${plural(offset, 'month')} after today's payment`;
}
